import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Table, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function UpgradePaths() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [upgradePaths, setUpgradePaths] = useState([]);

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load products and existing upgrade paths
    await fetchData();

    setLoading(false);
  };

  const fetchData = async () => {
    try {
      const response = await fetch('/api/upgrade-paths/get', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);

        // If we have saved upgrade paths, load them
        if (data.upgrade_paths && data.upgrade_paths.length > 0) {
          setUpgradePaths(data.upgrade_paths);
        } else {
          // Start with one empty row
          setUpgradePaths([createEmptyPath()]);
        }
      } else {
        setError('Failed to load data');
      }
    } catch (err) {
      console.error('Error loading upgrade paths:', err);
      setError('Error loading upgrade paths: ' + err.message);
    }
  };

  const createEmptyPath = () => ({
    id: Date.now() + Math.random(), // Unique ID for React key
    from_product_id: '',
    to_product_id: '',
    conversion_rate: '',
    time_value: '',
    time_unit: 'days'
  });

  const handleAddRow = () => {
    setUpgradePaths([...upgradePaths, createEmptyPath()]);
  };

  const handleRemoveRow = (id) => {
    if (upgradePaths.length === 1) {
      setError('At least one row is required. Clear the values if you don\'t want to define upgrade paths yet.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setUpgradePaths(upgradePaths.filter(path => path.id !== id));
  };

  const handlePathChange = (id, field, value) => {
    setUpgradePaths(upgradePaths.map(path => {
      if (path.id === id) {
        return { ...path, [field]: value };
      }
      return path;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      // Filter out empty rows (no from/to products selected)
      const validPaths = upgradePaths.filter(path =>
        path.from_product_id && path.to_product_id
      );

      // Validate conversion rates are 0-100
      const invalidRates = validPaths.filter(path => {
        const rate = parseFloat(path.conversion_rate);
        return isNaN(rate) || rate < 0 || rate > 100;
      });

      if (invalidRates.length > 0) {
        setError('Conversion rates must be between 0 and 100');
        setSaving(false);
        return;
      }

      // Validate time values are positive numbers
      const invalidTimes = validPaths.filter(path => {
        const timeVal = parseInt(path.time_value);
        return isNaN(timeVal) || timeVal < 0;
      });

      if (invalidTimes.length > 0) {
        setError('Timeframe values must be positive numbers');
        setSaving(false);
        return;
      }

      // Check for same product upgrade (from = to)
      const selfUpgrades = validPaths.filter(path =>
        path.from_product_id === path.to_product_id
      );

      if (selfUpgrades.length > 0) {
        setError('A product cannot upgrade to itself');
        setSaving(false);
        return;
      }

      const response = await fetch('/api/upgrade-paths/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          upgrade_paths: validPaths
        })
      });

      if (response.ok) {
        // Navigate back to dashboard
        navigate('/dashboard/business-model');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save upgrade paths');
      }
    } catch (err) {
      setError('Error saving upgrade paths: ' + err.message);
    }

    setSaving(false);
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    return product ? product.name : '';
  };

  const getProductPrice = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    return product ? `$${product.price}` : '';
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="business-model" />
      <Row className="justify-content-center">
        <Col md={12} lg={11}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2>Customer Journey Mapping</h2>
                  <p className="text-muted mb-0">
                    Define how customers upgrade from one product to another
                  </p>
                </div>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/business-model')}
                >
                  ← Back to Dashboard
                </Button>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {products.length === 0 ? (
                <Alert variant="warning">
                  <strong>No products found.</strong> Please complete the Product Catalog first.
                  <br />
                  <Button
                    variant="link"
                    className="p-0 mt-2"
                    onClick={() => navigate('/product-catalog')}
                  >
                    → Go to Product Catalog
                  </Button>
                </Alert>
              ) : (
                <>
                  <Alert variant="info" className="mb-4">
                    <strong>💡 How This Works:</strong>
                    <ul className="mb-0 mt-2">
                      <li>Define which products lead to which upgrades</li>
                      <li>Set the estimated percentage of customers who will upgrade</li>
                      <li>Specify how long it typically takes for customers to upgrade</li>
                      <li>You can have multiple upgrade paths from the same product (e.g., Module 1-5 → three different SaaS tiers)</li>
                    </ul>
                    <hr />
                    <p className="mb-0 small">
                      <strong>Example:</strong> "Module 1-5" ($97 one-time) → 20% upgrade to "$500/mo SaaS" within 30 days
                    </p>
                  </Alert>

                  <div className="mb-3">
                    <Button variant="primary" onClick={handleAddRow}>
                      + Add Upgrade Path
                    </Button>
                    <span className="text-muted ms-3 small">
                      {upgradePaths.length} upgrade path(s)
                    </span>
                  </div>

                  <div className="table-responsive">
                    <Table bordered hover>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '25%' }}>From Product</th>
                          <th style={{ width: '25%' }}>Upgrades To</th>
                          <th style={{ width: '15%' }}>% Who Upgrade</th>
                          <th style={{ width: '10%' }}>Time Value</th>
                          <th style={{ width: '15%' }}>Time Unit</th>
                          <th style={{ width: '10%' }} className="text-center">Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        {upgradePaths.map((path) => (
                          <tr key={path.id}>
                            <td>
                              <Form.Select
                                size="sm"
                                value={path.from_product_id}
                                onChange={(e) => handlePathChange(path.id, 'from_product_id', e.target.value)}
                              >
                                <option value="">Select product...</option>
                                {products.map(product => (
                                  <option key={product.id} value={product.id}>
                                    {product.name} ({getProductPrice(product.id)})
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={path.to_product_id}
                                onChange={(e) => handlePathChange(path.id, 'to_product_id', e.target.value)}
                              >
                                <option value="">Select product...</option>
                                {products.map(product => (
                                  <option key={product.id} value={product.id}>
                                    {product.name} ({getProductPrice(product.id)})
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={path.conversion_rate}
                                onChange={(e) => handlePathChange(path.id, 'conversion_rate', e.target.value)}
                                placeholder="0-100"
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                min="0"
                                value={path.time_value}
                                onChange={(e) => handlePathChange(path.id, 'time_value', e.target.value)}
                                placeholder="#"
                              />
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={path.time_unit}
                                onChange={(e) => handlePathChange(path.id, 'time_unit', e.target.value)}
                              >
                                <option value="days">Days</option>
                                <option value="months">Months</option>
                                <option value="years">Years</option>
                              </Form.Select>
                            </td>
                            <td className="text-center">
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleRemoveRow(path.id)}
                                title="Remove this upgrade path"
                              >
                                ×
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <Alert variant="secondary" className="mt-3">
                    <strong>💭 Notes:</strong>
                    <ul className="mb-0 mt-2 small">
                      <li>Leave rows blank if you don't have upgrade paths defined yet</li>
                      <li>The same product can appear multiple times in "From Product" with different upgrade destinations</li>
                      <li>Conversion rates are your best estimate - you can update them as you gather real data</li>
                      <li>This data will be used to calculate realistic revenue projections for your exit strategy</li>
                    </ul>
                  </Alert>

                  <div className="d-flex justify-content-between mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/dashboard/business-model')}
                    >
                      ← Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      disabled={saving}
                      onClick={handleSave}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Customer Journey →'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default UpgradePaths;
